import React, { useEffect, useState } from "react";
import AnalysisFieldCard from "./AnalysisFieldCard";
import AnalysisSection from "./AnalysisSection";
import { fieldLabel } from "./analysisFieldMeta";
import { formatRequestedBoolean, isBotManagerCombinedField } from "./analysisDisplayUtils";
import {
  formatFieldValue,
  getNestedValue,
  isFieldResult,
  shouldShowField,
  statusLabel,
} from "./analysisUtils";
import {
  ADC_FIELDS,
  ADC_EMBEDDED_APPSEC_FIELDS,
  ADC_INSTANCE_FIELD_NAMES,
  APPSEC_FIELDS,
  CLOUD_APPSEC_FIELDS,
  DDOS_CLOUD_FIELDS,
  DDOS_ONPREM_FIELDS,
  DDOS_PROJECT_TYPE,
  MANAGEMENT_FIELDS,
  MANAGEMENT_DETECTION_FIELDS,
  adcInstanceCardSummary,
  adcInstanceDisplayName,
  adcInstanceFieldLabel,
  adcInstanceSummaryText,
  hasAdcInstances,
  hasGlobalManagementCoverage,
  subsectionHasUsefulContent,
  visibleAdcInstances,
} from "./designSolutionUtils";

const BOT_MANAGER_MAIN_FIELD = "design.application_security.bot_manager_required";
const BOT_ADVANCED_FIELDS = [
  "design.application_security.sophisticated_bot_requirement_1",
  "design.application_security.sophisticated_bot_requirement_2",
];
// The combined card serves both ADCs with embedded AppSec and standalone
// Integrated/on-prem WAFs. The latter can have core appliance requirements
// (HA, throughput, TLS capacity, management) in addition to the embedded
// AppSec capability subset, so retain the full AppSec requirement set here.
const ONPREM_APPSEC_FIELDS = [...new Set([...APPSEC_FIELDS, ...ADC_EMBEDDED_APPSEC_FIELDS])];

function OrderedFieldList({ analysis, fieldIds, language, showOutOfScope, muted, onEvidenceClick }) {
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {fieldIds.map((fieldId) => {
        if (isBotManagerCombinedField(fieldId)) return null;
        const result = getNestedValue(analysis, fieldId.split("."));
        // Design cards are a concise requirement view: do not render fields
        // whose only result is “Not found in RFP”.
        if (!result || result.status === "not_found" || !shouldShowField(result, showOutOfScope)) return null;
        return (
          <AnalysisFieldCard
            key={fieldId}
            fieldId={fieldId}
            result={result}
            language={language}
            muted={muted}
            onEvidenceClick={onEvidenceClick}
          />
        );
      })}
    </div>
  );
}

function EvidenceChips({ ids, onEvidenceClick }) {
  if (!ids?.length) return null;
  return (
    <div className="mt-2 flex flex-wrap items-center gap-1.5">
      <span className="text-xs text-slate-400">Evidence:</span>
      {ids.map((id) => (
        <button
          key={id}
          type="button"
          className="rounded-md border border-slate-200 bg-slate-50 px-1.5 py-0.5 font-mono text-xs text-emerald-700 hover:bg-emerald-50 dark:border-slate-600 dark:bg-slate-800 dark:text-emerald-300 dark:hover:bg-emerald-950/40"
          onClick={() => onEvidenceClick?.(id)}
        >
          {id}
        </button>
      ))}
    </div>
  );
}

function BotManagerCombinedCard({ analysis, language, showOutOfScope, muted, onEvidenceClick }) {
  const botResult = getNestedValue(analysis, BOT_MANAGER_MAIN_FIELD.split("."));
  if (!botResult || botResult.status === "not_found" || !shouldShowField(botResult, showOutOfScope)) return null;

  const advancedRequirements = BOT_ADVANCED_FIELDS.map((fieldId) => ({
    fieldId,
    result: getNestedValue(analysis, fieldId.split(".")),
  })).filter(
    ({ result }) => result?.status === "found" && result.value != null && String(result.value).trim() !== ""
  );

  let mainDisplay;
  if (botResult.status === "not_found") {
    mainDisplay = statusLabel("not_found", language);
  } else if (botResult.status === "found" && typeof botResult.value === "boolean") {
    mainDisplay = formatRequestedBoolean(botResult.value, language);
  } else {
    mainDisplay = formatFieldValue(botResult.value, botResult, language, BOT_MANAGER_MAIN_FIELD);
  }

  const evidenceIds = [
    ...(botResult.evidence_row_ids || []),
    ...advancedRequirements.flatMap(({ result }) => result.evidence_row_ids || []),
  ];
  const uniqueEvidenceIds = [...new Set(evidenceIds)];

  return (
    <div
      data-testid="bot-manager-combined-card"
      className={`rounded-xl border px-4 py-3 ${
        muted
          ? "border-slate-200/80 bg-slate-50/40 opacity-75 dark:border-slate-700/80 dark:bg-slate-900/30"
          : "border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900/60"
      }`}
    >
      <p className="text-sm font-medium text-slate-800 dark:text-slate-100">
        {fieldLabel(BOT_MANAGER_MAIN_FIELD, language)}
      </p>
      <p className="mt-1.5 text-sm text-slate-700 dark:text-slate-200">{mainDisplay}</p>

      {advancedRequirements.length > 0 ? (
        <div className="mt-3 border-t border-slate-100 pt-3 dark:border-slate-800" data-testid="advanced-bot-requirements">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Advanced Bot Requirements
          </p>
          <ul className="mt-2 list-disc space-y-1 pl-4 text-sm text-slate-700 dark:text-slate-200">
            {advancedRequirements.map(({ fieldId, result }) => (
              <li key={fieldId}>{String(result.value)}</li>
            ))}
          </ul>
        </div>
      ) : null}

      {botResult.notes ? <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">{botResult.notes}</p> : null}
      <EvidenceChips ids={uniqueEvidenceIds} onEvidenceClick={onEvidenceClick} />
    </div>
  );
}

function ManagementCoverageNote({ analysis, fieldIds }) {
  if (!hasGlobalManagementCoverage(analysis, fieldIds)) return null;
  return (
    <p className="rounded-lg border border-sky-200 bg-sky-50/60 px-3 py-2 text-xs font-medium text-sky-800 dark:border-sky-900/50 dark:bg-sky-950/30 dark:text-sky-200">
      Management coverage: Some fields are covered by Cyber Controller global management.
    </p>
  );
}

export function DdosDesignContent({ analysis, language, showOutOfScope, muted, onEvidenceClick }) {
  const projectType = getNestedValue(analysis, DDOS_PROJECT_TYPE.split("."));
  const cloudInScope = analysis?.product_presence?.ddos_cloud === true;
  const cloudTitle = analysis?.product_presence?.ddos_cloud_service_provider
    ? "Cloud DDoS (service provider)"
    : "Cloud DDoS";
  const onpremInScope = analysis?.product_presence?.ddos_onprem !== false;
  const showCloud =
    showOutOfScope ||
    (cloudInScope && subsectionHasUsefulContent(analysis, ["design", "ddos", "cloud"]));
  const showOnprem =
    showOutOfScope ||
    (onpremInScope && subsectionHasUsefulContent(analysis, ["design", "ddos", "onprem"]));
  const mgmtFields = [...DDOS_CLOUD_FIELDS, ...DDOS_ONPREM_FIELDS].filter((id) =>
    /management_visibility|reporting_needs|logs_needs|storage_retention/.test(id)
  );

  return (
    <div className="space-y-4" data-testid="design-content-ddos">
      {projectType && shouldShowField(projectType, showOutOfScope) ? (
        <AnalysisFieldCard
          fieldId={DDOS_PROJECT_TYPE}
          result={projectType}
          language={language}
          muted={muted}
          onEvidenceClick={onEvidenceClick}
        />
      ) : null}
      {showCloud ? (
        <AnalysisSection
          title={cloudTitle}
          node={analysis?.design?.ddos?.cloud}
          pathPrefix="design.ddos.cloud"
          language={language}
          showOutOfScope={showOutOfScope}
          muted={muted}
          onEvidenceClick={onEvidenceClick}
        />
      ) : null}
      {showOnprem ? (
        <AnalysisSection
          title="On-premise DDoS"
          node={analysis?.design?.ddos?.onprem}
          pathPrefix="design.ddos.onprem"
          language={language}
          showOutOfScope={showOutOfScope}
          muted={muted}
          onEvidenceClick={onEvidenceClick}
        />
      ) : null}
      <ManagementCoverageNote analysis={analysis} fieldIds={mgmtFields} />
    </div>
  );
}

export function AppSecDesignContent({ analysis, language, showOutOfScope, muted, onEvidenceClick }) {
  const mgmtFields = CLOUD_APPSEC_FIELDS.filter((id) =>
    /management_visibility|reporting_needs|logs_needs|storage_retention/.test(id)
  );
  return (
    <div className="space-y-4" data-testid="design-content-appsec">
      <OrderedFieldList
        analysis={analysis}
        fieldIds={CLOUD_APPSEC_FIELDS}
        language={language}
        showOutOfScope={showOutOfScope}
        muted={muted}
        onEvidenceClick={onEvidenceClick}
      />
      <BotManagerCombinedCard
        analysis={analysis}
        language={language}
        showOutOfScope={showOutOfScope}
        muted={muted}
        onEvidenceClick={onEvidenceClick}
      />
      <ManagementCoverageNote analysis={analysis} fieldIds={mgmtFields} />
    </div>
  );
}

function InstanceFieldCard({ fieldName, result, language, muted, onEvidenceClick }) {
  if (!result || result.status === "not_found" || result.status === "not_applicable") return null;
  const fieldId = `design.alteon.${fieldName}`;
  return (
    <AnalysisFieldCard
      fieldId={fieldId}
      result={result}
      language={language}
      muted={muted}
      onEvidenceClick={onEvidenceClick}
      labelOverride={adcInstanceFieldLabel(fieldName, language)}
    />
  );
}

function AdcInstanceDetails({ instance, language, muted, onEvidenceClick }) {
  const fields = instance?.fields || {};

  return (
    <div className="space-y-4" data-testid="adc-instance-details">
      <div className="grid gap-3 sm:grid-cols-2">
        {ADC_INSTANCE_FIELD_NAMES.map((fieldName) => (
          <InstanceFieldCard
            key={fieldName}
            fieldName={fieldName}
            result={fields[fieldName]}
            language={language}
            muted={muted}
            onEvidenceClick={onEvidenceClick}
          />
        ))}
      </div>
      <EvidenceChips ids={instance.evidence_row_ids} onEvidenceClick={onEvidenceClick} />
    </div>
  );
}

function AdcInstancesView({ analysis, language, muted, onEvidenceClick }) {
  const instances = visibleAdcInstances(analysis);
  const [selectedIndex, setSelectedIndex] = useState(0);

  useEffect(() => {
    if (selectedIndex >= instances.length) {
      setSelectedIndex(0);
    }
  }, [instances.length, selectedIndex]);

  const summaryText = adcInstanceSummaryText(analysis);
  const selected = instances[selectedIndex] || instances[0];

  return (
    <div className="space-y-4" data-testid="adc-instances-view">
      {summaryText ? (
        <p className="text-sm font-medium text-slate-700 dark:text-slate-200" data-testid="adc-total-summary">
          {summaryText}
        </p>
      ) : null}

      <div className="flex flex-wrap gap-2" data-testid="adc-instance-cards">
        {instances.map((instance, index) => {
          const displayName = adcInstanceDisplayName(instance, instances);
          if (!displayName) return null;
          return (
          <button
            key={instance.instance_id || index}
            type="button"
            data-testid={`adc-instance-card-${instance.instance_id || index}`}
            aria-pressed={index === selectedIndex}
            onClick={() => setSelectedIndex(index)}
            className={`min-w-[12rem] rounded-2xl border px-4 py-3 text-left transition-colors ${
              index === selectedIndex
                ? "border-emerald-500 bg-emerald-50/80 shadow-sm dark:border-emerald-400 dark:bg-emerald-950/30"
                : muted
                  ? "border-slate-200 bg-slate-50/50 opacity-70 dark:border-slate-700 dark:bg-slate-900/30"
                  : "border-slate-200 bg-white hover:border-emerald-300 hover:bg-emerald-50/40 dark:border-slate-700 dark:bg-slate-900/60 dark:hover:border-emerald-700"
            }`}
          >
            <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">{displayName}</p>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{adcInstanceCardSummary(instance)}</p>
          </button>
          );
        })}
      </div>

      {selected ? (
        <AdcInstanceDetails
          instance={selected}
          language={language}
          muted={muted}
          onEvidenceClick={onEvidenceClick}
        />
      ) : null}
    </div>
  );
}

function EmbeddedAppsecDesign({ analysis, language, showOutOfScope, muted, onEvidenceClick }) {
  const presence = analysis?.product_presence || {};
  // Cloud AppSec remains on its dedicated Cloud AppSec card. Embedded and
  // on-prem WAF requirements belong on this combined ADC/on-prem card.
  const onPremAppsec = Boolean(presence.integrated_waf || presence.adc_embedded_appsec);
  if (!onPremAppsec) return null;
  const hasUsefulRequirement = ONPREM_APPSEC_FIELDS.some((fieldId) => {
    const result = getNestedValue(analysis, fieldId.split("."));
    return result?.status === "found";
  });
  if (!hasUsefulRequirement) return null;
  return (
    <section className="space-y-3" data-testid="adc-embedded-appsec-design">
      <h4 className="text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">AppSec Design</h4>
      <OrderedFieldList
        analysis={analysis}
        fieldIds={ONPREM_APPSEC_FIELDS}
        language={language}
        showOutOfScope={showOutOfScope}
        muted={muted}
        onEvidenceClick={onEvidenceClick}
      />
      <BotManagerCombinedCard
        analysis={analysis}
        language={language}
        showOutOfScope={showOutOfScope}
        muted={muted}
        onEvidenceClick={onEvidenceClick}
      />
    </section>
  );
}

export function AdcDesignContent({ analysis, language, showOutOfScope, muted, onEvidenceClick }) {
  if (hasAdcInstances(analysis)) {
    return (
      <div className="space-y-4" data-testid="design-content-adc">
        <AdcInstancesView analysis={analysis} language={language} muted={muted} onEvidenceClick={onEvidenceClick} />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="design-content-adc">
      <OrderedFieldList
        analysis={analysis}
        fieldIds={ADC_FIELDS}
        language={language}
        showOutOfScope={showOutOfScope}
        muted={muted}
        onEvidenceClick={onEvidenceClick}
      />
      <EmbeddedAppsecDesign
        analysis={analysis}
        language={language}
        showOutOfScope={showOutOfScope}
        muted={muted}
        onEvidenceClick={onEvidenceClick}
      />
    </div>
  );
}

function SolutionInstanceHeader({ instance }) {
  const warnings = instance?.warnings || [];
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        {(instance?.products || []).map((product) => (
          <span key={product} className="rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-900 dark:border-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-100">
            {product}
          </span>
        ))}
        {(instance?.components || []).map((component) => (
          <span key={component} className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs text-slate-600 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300">
            {component.replaceAll("_", " ")}
          </span>
        ))}
      </div>
      {warnings.map((warning) => (
        <p key={warning} className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-100">
          ⚠ {warning}
        </p>
      ))}
    </div>
  );
}

export function SolutionInstanceContent({ instance, language, showOutOfScope, muted, onEvidenceClick }) {
  const scopedAnalysis = {
    design: {
      application_security: instance?.fields?.application_security || {},
      alteon: instance?.fields?.alteon || {},
    },
    product_presence: {
      application_security: ["cloud_appsec", "integrated_waf", "onprem_appsec"].includes(instance?.type),
      cloud_waf: instance?.type === "cloud_appsec",
      integrated_waf: ["integrated_waf", "onprem_appsec"].includes(instance?.type),
      adc_embedded_appsec: ["alteon", "onprem_appsec"].includes(instance?.type) && Boolean(instance?.fields?.application_security),
      alteon: ["integrated_waf", "alteon", "onprem_appsec"].includes(instance?.type),
    },
  };
  const props = { analysis: scopedAnalysis, language, showOutOfScope, muted, onEvidenceClick };
  return (
    <div className="space-y-6" data-testid={`design-content-${instance?.type || "solution"}`}>
      <SolutionInstanceHeader instance={instance} />
      {instance?.type === "cloud_appsec" ? <AppSecDesignContent {...props} /> : null}
      {["integrated_waf", "alteon", "onprem_appsec"].includes(instance?.type) ? <AdcDesignContent {...props} /> : null}
    </div>
  );
}

export function ManagementDesignContent({ analysis, language, showOutOfScope, muted, onEvidenceClick }) {
  const detectionVisible = MANAGEMENT_DETECTION_FIELDS.some((id) => {
    const result = getNestedValue(analysis, id.split("."));
    return result && shouldShowField(result, showOutOfScope);
  });
  const detectionTitle =
    language === "es"
      ? "Detección, telemetría y desvío"
      : language === "pt"
        ? "Detecção, telemetria e desvio"
        : "Detection, telemetry, and diversion";
  return (
    <div className="space-y-6" data-testid="design-content-management">
      <OrderedFieldList
        analysis={analysis}
        fieldIds={MANAGEMENT_FIELDS}
        language={language}
        showOutOfScope={showOutOfScope}
        muted={muted}
        onEvidenceClick={onEvidenceClick}
      />
      {detectionVisible ? (
        <div className="space-y-3" data-testid="management-detection-chapter">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            {detectionTitle}
          </h3>
          <OrderedFieldList
            analysis={analysis}
            fieldIds={MANAGEMENT_DETECTION_FIELDS}
            language={language}
            showOutOfScope={showOutOfScope}
            muted={muted}
            onEvidenceClick={onEvidenceClick}
          />
        </div>
      ) : null}
    </div>
  );
}

export function DesignSolutionContent({ solutionId, analysis, language, showOutOfScope, muted, onEvidenceClick }) {
  const props = { analysis, language, showOutOfScope, muted, onEvidenceClick };
  if (solutionId?.startsWith("solution:")) {
    const instanceId = solutionId.slice("solution:".length);
    const instances = analysis?.design?.solutions || [];
    const instance = instanceId === "onprem-appsec"
      ? {
          id: instanceId,
          type: "onprem_appsec",
          products: [...new Set(instances.filter((item) => ["integrated_waf", "alteon"].includes(item.type)).flatMap((item) => item.products || []))],
          fields: instances.filter((item) => ["integrated_waf", "alteon"].includes(item.type)).reduce((combined, item) => ({
            application_security: { ...combined.application_security, ...(item.fields?.application_security || {}) },
            alteon: { ...combined.alteon, ...(item.fields?.alteon || {}) },
          }), { application_security: {}, alteon: {} }),
          evidence_row_ids: [...new Set(instances.filter((item) => ["integrated_waf", "alteon"].includes(item.type)).flatMap((item) => item.evidence_row_ids || []))],
        }
      : instances.find((item) => item.id === instanceId);
    return <SolutionInstanceContent instance={instance} {...props} />;
  }
  switch (solutionId) {
    case "ddos":
      return <DdosDesignContent {...props} />;
    case "appsec":
      return <AppSecDesignContent {...props} />;
    case "adc":
      return <AdcDesignContent {...props} />;
    case "management":
      return <ManagementDesignContent {...props} />;
    default:
      return null;
  }
}

export function ExecutiveSummaryNarrative({ exec, language, onEvidenceClick }) {
  if (!exec || !isFieldResult(exec)) {
    return (
      <p className="text-slate-500">
        {language === "es"
          ? "No hay resumen ejecutivo disponible."
          : language === "pt"
            ? "Nenhum resumo executivo disponível."
            : "No executive summary available."}
      </p>
    );
  }
  const paragraphs = Array.isArray(exec.value)
    ? exec.value.filter(Boolean)
    : exec.value
      ? [String(exec.value)]
      : [];
  if (!paragraphs.length) {
    return (
      <p className="text-slate-500">
        {language === "es"
          ? "No hay resumen ejecutivo disponible."
          : language === "pt"
            ? "Nenhum resumo executivo disponível."
            : "No executive summary available."}
      </p>
    );
  }
  const confidenceLabel =
    language === "es" ? "Confianza" : language === "pt" ? "Confiança" : "Confidence";
  const evidenceLabel =
    language === "es" ? "Evidencia" : language === "pt" ? "Evidência" : "Evidence";
  return (
    <div data-testid="executive-summary-narrative">
      {paragraphs.map((paragraph, index) => (
        <p
          key={index}
          className={`text-[15px] leading-relaxed text-slate-700 dark:text-slate-200 ${
            index < paragraphs.length - 1 ? "mb-4" : ""
          }`}
        >
          {paragraph}
        </p>
      ))}
      {(exec.notes || exec.confidence || exec.evidence_quality) && (
        <p className="mt-4 border-t border-slate-100 pt-3 text-xs text-slate-500 dark:border-slate-800 dark:text-slate-400">
          {exec.confidence ? `${confidenceLabel}: ${exec.confidence}` : null}
          {exec.confidence && exec.evidence_quality ? " · " : null}
          {exec.evidence_quality ? `${evidenceLabel}: ${exec.evidence_quality}` : null}
          {exec.notes ? ` — ${exec.notes}` : null}
        </p>
      )}
      {exec.source_strategy === "synthesized_from_analysis" ? null : exec.evidence_row_ids?.length ? (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {exec.evidence_row_ids.map((id) => (
            <button
              key={id}
              type="button"
              className="rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5 font-mono text-[11px] text-slate-600 hover:bg-slate-100 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300"
              onClick={() => onEvidenceClick?.(id)}
            >
              {id}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
